import { ReactNode, useEffect } from "react";

import { Outlet, useLocation } from "react-router-dom";
import { useAuthStore } from "@/stores/auth-store";
import { useImageConfigStore } from "@/stores/image-config-store";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  NavigationProvider,
  useNavigation,
} from "@/contexts/NavigationContext";

import Footer from "../components/Footer";
import Header from "@/components/header/Header";
import HomepageHeader from "@/components/header/HomepageHeader";

interface MainLayoutProps {
  children?: ReactNode;
}

// Inner component to access navigation context
const LayoutContent = ({ children }: { children: ReactNode }) => {
  const navigationProps = useNavigation();
  const isMobile = useIsMobile();

  const location = useLocation();
  const isHomePage = location.pathname === "/";
  const isVisionPurposePage =
    location.pathname === "/corporate/about/our-vision-purpose-values";
  const hasHeader = !isVisionPurposePage;

  const whiteBgPages = [
    "/terms-of-use",
    "/terms-and-conditions-of-sale",
    "/privacy-policy",
    "/copyright-and-legal",
    "/boring-legal-stuff",
  ];
  const isWhiteBgPage = whiteBgPages.includes(location.pathname);

  // Different padding for mobile vs desktop header heights
  const headerPadding = isHomePage ? (isMobile ? "pt-20" : "pt-10") : "pt-0"; // 80px mobile, 128px desktop

  return (
    <div className={isWhiteBgPage ? "flex flex-col min-h-screen bg-white" : "contents"}>
      {hasHeader &&
        (isHomePage && isMobile ? (
          <HomepageHeader />
        ) : (
          <Header
            categories={navigationProps.categories}
            activeCategory={navigationProps.activeCategory}
            onCategoryChange={navigationProps.onCategoryChange}
          />
        ))}

      <main className={`overflow-visible ${headerPadding}${isWhiteBgPage ? " flex-1" : " grow"}`}>
        {children}
      </main>
      <Footer />
    </div>
  );
};

const MainLayout = ({ children }: MainLayoutProps) => {
  const initialize = useAuthStore((s) => s.initialize);
  const getUrl = useImageConfigStore((s) => s.getUrl);

  const backgroundPatternUrl = getUrl("global.background.pattern");

  useEffect(() => {
    void initialize();
  }, [initialize]);

  return (
    <>
      {/* ================================================================
          LAYER 1: Fixed Background (never scrolls)
          - position:fixed covers entire viewport
          - z-index:-1 places it behind all content
          - Uses pattern image from config
          - Mobile Safari hardened with transform:translateZ(0)
          ================================================================ */}
      <div
        className="fixed-background-layer"
        style={{
          backgroundImage: backgroundPatternUrl
            ? `url("${backgroundPatternUrl}")`
            : undefined,
        }}
        aria-hidden="true"
      />

      {/* ================================================================
          LAYER 2: Scrolling Content Wrapper
          - Contains all page content (header, main, footer)
          - Scrolls normally with native browser behavior
          - position:relative ensures proper stacking above background
          - CRITICAL: Creates stacking context for sticky header
          ================================================================ */}
      <div>
        <NavigationProvider>
          <LayoutContent>{children || <Outlet />}</LayoutContent>
        </NavigationProvider>
      </div>
    </>
  );
};

export default MainLayout;
